import React, { useState, useRef } from "react"
import PropTypes from "prop-types"
import styled from "@emotion/styled"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import FormControl from "@mui/material/FormControl"
import LoadingScreen from "components/common/LoadingScreen"
import { useSnackbar } from "notistack"
import { useForm } from "react-hook-form"
import { addSubCategory, updateSubCategory } from "services/category"

const StyledSubCategoryDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(620px, calc(100vw - 30px));
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 22px 48px rgba(15, 23, 42, 0.18);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 12px;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .MuiDialogContent-root {
    padding: 8px 22px 12px;
  }

  .MuiDialogActions-root {
    padding: 10px 22px 20px;
    gap: 10px;
  }

  .dialog-field-group {
    margin-top: 8px;
  }

  .dialog-select {
    margin-bottom: 14px;
  }

  .MuiInputBase-root {
    border-radius: 10px;
    background-color: var(--cms-input-bg);
  }

  .dialog-cancel-btn {
    text-transform: none;
    font-weight: 700;
    color: var(--cms-text-secondary);
  }

  .dialog-submit-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22);
  }
`

const defaultSubCategoryValues = { name: "", description: "" }

const SubCategoryDlg = (props) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: defaultSubCategoryValues,
  })
  const { onSubmit, categories } = props
  const [categoryId, setCategoryId] = useState(null)
  const [subCategoryId, setSubCategoryId] = useState(null)
  const targetSubCategoryRefreshRef = useRef()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const getSubmitErrorMessage = (error, fallbackMessage) => {
    const serverMessage = error?.response?.data?.message
    if (Array.isArray(serverMessage)) {
      return serverMessage.join(", ")
    }
    return serverMessage || error?.message || fallbackMessage
  }

  const handleClose = () => {
    setOpen(false)
  }

  const openDlg = (cateId, editCategory, subCategoryRefresh) => {
    const hasEditTarget = Boolean(editCategory)
    setCategoryId(hasEditTarget ? editCategory.categoryId : cateId)
    setSubCategoryId(hasEditTarget ? editCategory.id : null)
    setIsEditMode(hasEditTarget)
    targetSubCategoryRefreshRef.current = subCategoryRefresh
    reset(
      hasEditTarget
        ? {
            name: editCategory.name || "",
            description: editCategory.description || "",
          }
        : defaultSubCategoryValues
    )
    setOpen(true)
  }

  const handleAction = async (data) => {
    const fallbackErrorMessage = isEditMode
      ? "Update Subcategory Fail"
      : "Create Subcategory Fail"

    setLoading(true)
    try {
      const response = isEditMode
        ? await updateSubCategory({ ...data, categoryId, id: subCategoryId })
        : await addSubCategory({ ...data, categoryId })

      if (response.data.rtnCode === 200) {
        if (targetSubCategoryRefreshRef.current) {
          targetSubCategoryRefreshRef.current()
        }
        enqueueSnackbar(
          isEditMode
            ? "Update Subcategory success"
            : "Create Subcategory success",
          { variant: "success" }
        )
        onSubmit()
        handleClose()
        return
      }

      enqueueSnackbar(response?.data?.message || fallbackErrorMessage, {
        variant: "warning",
      })
    } catch (error) {
      console.error("SubCategory submit error:", error)
      enqueueSnackbar(getSubmitErrorMessage(error, fallbackErrorMessage), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const dlg = (
    <StyledSubCategoryDialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          {isEditMode ? "Edit Subcategory" : "Add Subcategory"}
        </DialogTitle>
        <DialogContent>
          <div className="dialog-field-group">
            {isEditMode && (
              <FormControl className="dialog-select" fullWidth size="small">
                <InputLabel id="sub-category-id-select-label">Category</InputLabel>
                <Select
                  fullWidth
                  labelId="sub-category-id-select-label"
                  id="sub-category-id-select"
                  value={categoryId || ""}
                  label="Category"
                  onChange={(e) => {
                    setCategoryId(Number(e.target.value))
                  }}
                >
                  <MenuItem key={`category-select-item-all`} value={-1}>
                    All
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem
                      key={`category-select-item-${category.id}`}
                      value={category.id}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              autoFocus
              autoComplete="off"
              id="sub-cat-name"
              label="Subcategory Name"
              type="text"
              fullWidth
              {...register("name", { required: true })}
              error={!!errors?.name}
              helperText={!!errors?.name ? "Subcategory name is required" : " "}
              variant="outlined"
            />

            <TextField
              autoComplete="off"
              id="sub-cat-description"
              label="Subcategory Description"
              type="text"
              fullWidth
              multiline
              minRows={3}
              {...register("description", { required: true })}
              error={!!errors?.description}
              helperText={
                !!errors?.description ? "Description is required" : " "
              }
              variant="outlined"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button className="dialog-cancel-btn" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="medium"
            className="dialog-submit-btn"
            disabled={loading}
          >
            {isEditMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
      <LoadingScreen loading={loading} />
    </StyledSubCategoryDialog>
  )

  return [openDlg, dlg, loading]
}

SubCategoryDlg.propTypes = {
  onSubmit: PropTypes.func,
}

SubCategoryDlg.defaultProps = {
  onSubmit: null,
}

export default SubCategoryDlg
